import React, { useState, useEffect } from 'react';
import { Bell, Plus, Trash2, Save, Send, Loader2, AlertCircle, Check } from 'lucide-react';
import { getNotificationConfig, saveNotificationConfig, sendImmediateNotification } from '../services/notificationService';
import './NotificationsManager.css';

interface NotificationConfig {
  enabled: boolean;
  intervalHours: number;
  messages: string[];
}

const NotificationsManager: React.FC = () => {
  const [config, setConfig] = useState<NotificationConfig>({
    enabled: true,
    intervalHours: 24,
    messages: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedConfig = await getNotificationConfig();
      if (loadedConfig) {
        setConfig(loadedConfig);
      } else {
        // Initialize with defaults if no config exists
        setConfig({
          enabled: true,
          intervalHours: 24,
          messages: [
            "Ready for a new challenge? 🎮",
            "הגיע הזמן לצעוד בשביל ישראל! 🇮🇱",
            "Time to hike the Israel National Trail! 🇮🇱",
            "New puzzles are waiting for you! 🎯"
          ]
        });
      }
    } catch (err) {
      console.error('Failed to load notification config:', err);
      setError('Failed to load notification configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await saveNotificationConfig(config);
      setSuccess('Notification settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save notification config:', err);
      setError('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (config.messages.length === 0) {
      setError('Add at least one message before sending');
      return;
    }

    if (!window.confirm('Send notification to all users immediately?')) {
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(null);

      // Get random message
      const randomMessage = config.messages[Math.floor(Math.random() * config.messages.length)];

      await sendImmediateNotification(randomMessage);
      setSuccess('Notification sent to all devices!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Failed to send notification:', err);
      setError('Failed to send notification. Check Firebase Cloud Messaging setup.');
    } finally {
      setSending(false);
    }
  };

  const handleAddMessage = () => {
    if (newMessage.trim() === '') {
      return;
    }
    setConfig({
      ...config,
      messages: [...config.messages, newMessage.trim()]
    });
    setNewMessage('');
  };

  const handleRemoveMessage = (index: number) => {
    setConfig({
      ...config,
      messages: config.messages.filter((_, i) => i !== index)
    });
  };

  const handleToggleEnabled = () => {
    setConfig({
      ...config,
      enabled: !config.enabled
    });
  };

  const handleIntervalChange = (hours: number) => {
    setConfig({
      ...config,
      intervalHours: Math.max(1, hours)
    });
  };

  if (loading) {
    return (
      <div className="notifications-manager">
        <div className="loading-container">
          <Loader2 size={48} className="animate-spin" />
          <p>Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-manager">
      <div className="notifications-header">
        <div className="header-title">
          <Bell size={24} />
          <h2>Notifications Manager</h2>
        </div>
        <p className="header-description">
          Configure push notifications that will be sent to users
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Check size={20} />
          <span>{success}</span>
        </div>
      )}

      <div className="notifications-content">
        {/* Settings Section */}
        <div className="settings-section">
          <h3>General Settings</h3>

          <div className="setting-item">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={handleToggleEnabled}
                className="toggle-input"
              />
              <span className="toggle-switch"></span>
              <span className="toggle-text">
                Notifications {config.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
            <p className="setting-description">
              When disabled, no automatic notifications will be sent to users
            </p>
          </div>

          <div className="setting-item">
            <label>
              Notification Interval (hours)
              <input
                type="number"
                min="1"
                value={config.intervalHours}
                onChange={(e) => handleIntervalChange(parseInt(e.target.value) || 24)}
                className="interval-input"
              />
            </label>
            <p className="setting-description">
              Time between notifications after user last opened the app
            </p>
          </div>
        </div>

        {/* Messages Section */}
        <div className="messages-section">
          <h3>Notification Messages</h3>
          <p className="section-description">
            Add multiple messages. One will be randomly selected for each notification.
          </p>

          <div className="add-message-form">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddMessage()}
              placeholder="Enter a notification message..."
              className="message-input"
              maxLength={100}
            />
            <button
              onClick={handleAddMessage}
              className="btn btn-secondary"
              disabled={newMessage.trim() === ''}
            >
              <Plus size={18} />
              Add
            </button>
          </div>

          <div className="messages-list">
            {config.messages.length === 0 ? (
              <div className="empty-state">
                <Bell size={48} className="empty-icon" />
                <p>No notification messages yet</p>
                <p className="empty-hint">Add messages that will be sent to users</p>
              </div>
            ) : (
              config.messages.map((message, index) => (
                <div key={index} className="message-item">
                  <span className="message-number">{index + 1}</span>
                  <span className="message-text">{message}</span>
                  <button
                    onClick={() => handleRemoveMessage(index)}
                    className="btn-icon btn-danger"
                    title="Remove message"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="message-count">
            {config.messages.length} message{config.messages.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Actions Section */}
        <div className="actions-section">
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={saving || config.messages.length === 0}
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Settings
              </>
            )}
          </button>

          <button
            onClick={handleSendNow}
            className="btn btn-warning"
            disabled={sending || config.messages.length === 0 || !config.enabled}
          >
            {sending ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Notification Now
              </>
            )}
          </button>

          <p className="action-hint">
            "Send Now" will immediately send a notification to all users with the app installed
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationsManager;
